import { Box, Autocomplete, TextField } from '@mui/material';
import { Typography } from '@sweep-io/sweep-design';
import { useCallback, useState, useEffect } from 'react';
import { TeamsRecipientInfo, TeamsRecipientType } from '../teams/types';
import { useIntegrations } from '../../../hooks/useIntegrations';
import { autocompleteStyles } from './styles';

interface TeamsTeamSelectorProps {
  onChange: (teamId: string) => void;
  readonly?: boolean;
  customButtonText?: string;
  teamId?: string;
}

export const TeamsTeamSelector = ({
  onChange,
  readonly,
  customButtonText = 'Select Team',
  teamId,
}: TeamsTeamSelectorProps) => {
  const [teams, setTeams] = useState<TeamsRecipientInfo[]>([]);
  const [selectedValue, setSelectedValue] = useState<TeamsRecipientInfo | null>(null);
  const { getTeamsRecipients } = useIntegrations();

  const fetchTeams = useCallback(async () => {
    const teamsList = await getTeamsRecipients(TeamsRecipientType.TEAM);
    setTeams(teamsList);
  }, [getTeamsRecipients]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  useEffect(() => {
    if (teamId) {
      const team = teams.find((t) => {
        const teamIdToCompare = Array.isArray(t.id) ? t.id[0] : t.id;
        return teamIdToCompare === teamId;
      });
      setSelectedValue(team || null);
    } else {
      setSelectedValue(null);
    }
  }, [teamId, teams]);

  const handleChange = useCallback(
    (_: any, newValue: TeamsRecipientInfo | null) => {
      setSelectedValue(newValue);
      if (newValue) {
        const newTeamId = Array.isArray(newValue.id) ? newValue.id[0] : newValue.id;
        onChange(newTeamId);
      } else {
        onChange(''); // Call onChange with empty string when cleared
      }
    },
    [onChange],
  );

  return (
    <Box sx={autocompleteStyles}>
      <Autocomplete
        disablePortal
        disabled={readonly}
        options={teams}
        value={selectedValue}
        onChange={handleChange}
        getOptionLabel={(option) => option.label}
        renderInput={(params) => (
          <TextField {...params} placeholder={customButtonText} variant="outlined" fullWidth />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Typography variant="body">{option.label}</Typography>
          </Box>
        )}
        isOptionEqualToValue={(option, value) => {
          if (!value) return false;
          const optionId = Array.isArray(option.id) ? option.id[0] : option.id;
          const valueId = Array.isArray(value.id) ? value.id[0] : value.id;
          return optionId === valueId;
        }}
      />
    </Box>
  );
};
